import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import TopBar from '../components/TopBar.jsx';
import { apiGet } from '../lib/api.js';
import SubscriptionPanel from './billing/SubscriptionPanel.jsx';
import FundsPanel from './billing/FundsPanel.jsx';
import BillingHistory from './billing/BillingHistory.jsx';
import PaymentMethodsPanel from './billing/PaymentMethodsPanel.jsx';
import PlansAndPromo from './billing/PlansAndPromo.jsx';

const TABS = [['billing', 'Billing'], ['methods', 'Payment Methods']];

/** Toasts for the Stripe Checkout redirects (?topup / ?sub / ?card), then cleans the URL. */
function useCheckoutFlags(onChanged) {
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const flags = [
      ['topup', 'success', () => toast.success('Top-up successful — credit will appear shortly.')],
      ['topup', 'cancelled', () => toast('Top-up cancelled.', { icon: '↩' })],
      ['sub', 'success', () => toast.success('Subscription started.')],
      ['card', 'ok', () => toast.success('Card saved. You’re all set for usage.')],
      ['card', 'cancel', () => toast('Card setup cancelled.', { icon: '↩' })],
    ];
    let hit = false;
    for (const [k, v, fn] of flags) if (p.get(k) === v) { fn(); hit = true; }
    if (hit) { window.history.replaceState({}, '', window.location.pathname); onChanged(); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Billing page: subscription, funds, payment methods and history. */
export default function Billing() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('billing');
  const { data: overview, isLoading, isError } = useQuery({
    queryKey: ['billing-overview'],
    queryFn: () => apiGet('/billing-center/overview'),
    staleTime: 10_000,
  });
  useCheckoutFlags(() => qc.invalidateQueries({ queryKey: ['billing-overview'] }));

  return (
    <div className="space-y-4">
      <TopBar title="Billing" subtitle="Subscription, funds & payment methods" />
      <div className="flex gap-1 border-b border-white/10">
        {TABS.map(([id, label]) => (
          <button
            key={id} type="button" onClick={() => setTab(id)}
            className={`px-5 py-2 text-sm font-semibold border-b-2 ${tab === id ? 'border-civix-purple text-white' : 'border-transparent text-white/50 hover:text-white'}`}
          >{label}</button>
        ))}
      </div>

      {isLoading && <div className="panel p-6 text-sm text-white/50">Loading billing…</div>}
      {isError && <div className="panel p-6 text-sm text-red-300">Could not load billing. Please refresh.</div>}

      {overview && tab === 'billing' && (
        <div className="grid xl:grid-cols-2 gap-4 items-start">
          <div className="space-y-4">
            <SubscriptionPanel overview={overview} />
            {overview.isAdmin && <PlansAndPromo subscription={overview.subscription} />}
          </div>
          <div className="space-y-4">
            <FundsPanel overview={overview} onManageCards={() => setTab('methods')} />
            <BillingHistory />
          </div>
        </div>
      )}
      {overview && tab === 'methods' && <PaymentMethodsPanel overview={overview} />}
    </div>
  );
}
